import { readFormation, type FormationCase } from "./formationCells";
import { type NotesBlock } from "./grammarNotes";

/**
 * The two blocks that 文法 and Read's dock BOTH draw (日本-524).
 *
 * They live apart from `GrammarEntryBody` for one reason: `components/dictionary/GrammarCard.tsx`
 * renders the same 接続 and the same 解説 in Read's dock, and CLAUDE.md's rule for this pair is that
 * whatever 文法 gains should reach Read. A second implementation of either would drift — Read's
 * copy of 接続 was `{entry.formation}` in a `<span>` and its 解説 a `pre-line` paragraph, which is
 * exactly the state this ticket removes from the other surface.
 *
 * `ResultList.tsx` is the precedent for the direction of the import: `components/dictionary/`
 * already reaches into `surfaces/` for `ResultCard`, `AddedPanel`, `onCard` and `cardSignal`.
 */

/**
 * 接続 as the deck's own table.
 *
 * **It is a table, and this draws it as one** — see `formationCells.ts` for the measurement (522 of
 * 523 entries carry `<table><tr><td>` in the deck). A case's pattern rows sit above its examples,
 * and an example's translation sits under it in `--ink-4` rather than in a column of its own: at
 * 360px a third column would wrap every gloss to three lines.
 */
export function FormationTable({ cases }: { cases: FormationCase[] }) {
  return (
    <div
      data-check="grammar.formation"
      className="flex flex-col gap-[10px] rounded-[var(--r-2)] border border-line bg-sunken px-[13px] py-[11px]"
    >
      {cases.map((one, index) => (
        <div key={index} className="flex flex-col gap-[5px]">
          {one.label && (
            <span data-check="grammar.formation.case" className="font-mono text-meta text-ink-4">
              {one.label}
            </span>
          )}
          {one.rows.map((row, rowIndex) => (
            <div key={rowIndex} className="flex flex-col gap-[2px]">
              <div className="flex flex-wrap items-baseline gap-x-[8px]">
                {row.label && (
                  <span className="font-jp text-[15px]/[1.4] text-ink-3">{row.label}</span>
                )}
                <span className="font-jp text-[17px]/[1.4] text-ink">{row.text}</span>
              </div>
              {row.gloss && <span className="text-meta text-ink-4">{row.gloss}</span>}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/** 接続 for the one entry whose field carries no table — 日本-519's flat lines. */
export function FormationLines({ lines }: { lines: string[] }) {
  return (
    <div
      data-check="grammar.formation"
      className="inline-flex flex-col gap-[5px] rounded-[var(--r-2)] border border-line bg-sunken px-[14px] py-[10px]"
    >
      {lines.map((line, index) => (
        <span key={index} className="font-jp text-[17px]/[1.4] text-ink">
          {line}
        </span>
      ))}
    </div>
  );
}

/**
 * One block of 解説.
 *
 * **Every kind is set differently and that IS the feature.** A note hangs its figure in the margin
 * so the prose has a left edge; a sentence from the book is set in Japanese at 17px with its
 * translation quiet underneath; a cross-reference is a row of chips rather than a parenthesis
 * inside a paragraph. Read as one undifferentiated `pre-line` blob, none of that was visible.
 *
 * `preformatted` sets a block in mono with its spacing kept — the book drew ten timeline diagrams
 * in ASCII and proportional type with collapsed whitespace destroys them.
 */
/**
 * Where a note's BODY starts, and therefore where everything under a note is indented to.
 *
 * It is the marker column plus its gap — 42 + 10 — so the two move together. Set apart as a
 * constant because they were two independent numbers that had to agree, and widening one without
 * the other silently un-hangs every example and paragraph beneath a note.
 */
const NOTE_HANG = "pl-[52px]";

/**
 * A marker as the reader should see it: `(2).` → `(2)`, and `b.` unchanged.
 *
 * **The book punctuates its own NUMBERING inconsistently** — both `(2) 休みの間…` and `(2). 休みの間…`
 * occur, in the same entry — so a faithful render puts `(1)` above `(2).` in one list, which reads
 * as a typo of ours rather than of the book's.
 *
 * **Its lettering is not inconsistent, so a letter keeps its period.** Stripping unconditionally
 * drew `b` in the gutter beside an `a.` still inline in the sentence above it — advanced-617 is
 * full of that shape, `[4] a. 友美は…` followed by `b. その店は…` — and one series lettered two ways
 * reads as a rendering fault. Only a bracketed marker is normalised.
 *
 * The parser deliberately does NOT do either: its corpus test asserts it never drops a character,
 * and presentation is this side's business.
 */
function markerLabel(marker: string): string {
  return /^[([]/.test(marker) ? marker.replace(/\.$/, "") : marker;
}

export function NotesBlockView({ block }: { block: NotesBlock }) {
  const pre = "preformatted" in block && block.preformatted;
  const proseClass = `m-0 text-[16px]/[1.65] text-ink-2 ${
    pre ? "whitespace-pre font-mono overflow-x-auto" : ""
  }`;

  switch (block.kind) {
    case "note":
      return (
        <div data-check="grammar.notes.note" className="flex gap-[10px]">
          {/* A hanging figure: the marker sits in the margin so every line of the note shares a
              left edge, which is what makes a run of notes scannable at a glance.

              **42px is the widest marker this branch emits, not a round number.** It draws roman
              sections as well as numbered notes, and the corpus reaches FIVE characters — `VIII.`
              and `VII.` once each, `III.` 41 times, plus `IV.`, `VI.`, `10.` and `11.`. IBM Plex
              Mono advances 0.6em, so at `text-meta`'s 14px that is 8.4px a character and 42px for
              five: at the 22px this started as, `VIII.` overran the 10px gap and painted over the
              body. It costs ~20px of measure on the common single-digit case, which is the right
              side of the trade — a marker printed over its own prose is a rendering fault, and
              letting the column size to its content would rag the left edge the hanging figure
              exists to keep straight. `NOTE_HANG` below moves with it. */}
          <span className="w-[42px] shrink-0 pt-[2px] font-mono text-meta text-ink-4">
            {block.marker}
          </span>
          <p className={`${proseClass} min-w-0 flex-1`}>{block.body}</p>
        </div>
      );

    case "example":
      return (
        <div data-check="grammar.notes.example" className={`flex gap-[10px] ${NOTE_HANG}`}>
          {/* 34px: four characters, which is what `(10)`…`(15)` and `[10]`…`[13]` need — 31
              blocks in the corpus, every one of which overran the 26px this started as. */}
          {block.marker && (
            <span className="w-[34px] shrink-0 pt-[3px] font-mono text-meta text-ink-4">
              {markerLabel(block.marker)}
            </span>
          )}
          <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
            <span
              className={`font-jp text-[17px]/[1.6] text-ink-2 ${pre ? "whitespace-pre overflow-x-auto font-mono" : ""}`}
            >
              {block.ja}
            </span>
            {block.en && <span className="text-meta text-ink-4">{block.en}</span>}
          </div>
        </div>
      );

    case "header":
      return (
        <h4
          data-check="grammar.notes.header"
          className="m-0 mt-[8px] border-t border-line-soft pt-[14px] font-jp text-meta font-semibold tracking-[.1em] text-ink-3"
        >
          {block.text}
        </h4>
      );

    case "crossRef":
      return (
        <div data-check="grammar.notes.crossref" className="flex flex-wrap items-center gap-[6px]">
          <span className="text-meta text-ink-4">⇨</span>
          {block.targets.map((target, index) => (
            <span
              key={index}
              className="rounded-[var(--r-2)] border border-line bg-line-soft px-[8px] py-[3px] font-jp text-meta text-ink-3"
            >
              {target}
            </span>
          ))}
        </div>
      );

    case "prose":
      return (
        <p data-check="grammar.notes.prose" className={`${proseClass} ${NOTE_HANG}`}>
          {block.text}
        </p>
      );
  }
}


/**
 * 接続, whichever shape the data is in. One call site for both surfaces.
 */
export function FormationBlock({ formation }: { formation: string }) {
  const read = readFormation(formation);
  return read.kind === "cases" ? (
    <FormationTable cases={read.cases} />
  ) : (
    <FormationLines lines={read.lines} />
  );
}

/**
 * 解説, set. `className` caps the line length — **the whole point of the pass**, since the field
 * was running to 150–210 characters a line at 14px before.
 *
 * **It takes the parsed blocks rather than the raw field**, because `GrammarEntryBody` already has
 * them: it decides whether to draw the 解説 label at all from `blocks.length`, and parsing here as
 * well ran `notesBlocks` twice over a field that reaches 9,646 characters — on every keystroke in
 * 文法's search box, since `query` is a prop of the surface the open entry renders inside.
 */
export function NotesBody({
  blocks,
  className = "",
}: {
  blocks: readonly NotesBlock[];
  className?: string;
}) {
  return (
    <div
      data-check="grammar.notes"
      className={`flex flex-col gap-[13px] text-[16px]/[1.65] ${className}`}
    >
      {blocks.map((block, index) => (
        <NotesBlockView key={index} block={block} />
      ))}
    </div>
  );
}
